import { Card, Button, Icon } from '../atoms'
import type { POICandidate } from '../../types'
import { haversineMeters } from '../../utils/geo'

export interface POIDetailPanelProps {
  poi: POICandidate | null
  userPosition: { lat: number; lon: number } | null
  onClose: () => void
  onRoute: (poi: POICandidate) => void
  onNarrate?: (poi: POICandidate) => void
  language: string
  narratingPOI?: string | null
}

export const POIDetailPanel = ({
  poi,
  userPosition,
  onClose,
  onRoute,
  onNarrate,
  language,
  narratingPOI,
}: POIDetailPanelProps) => {
  if (!poi) return null

  const isNarrating = narratingPOI === poi.key

  const distance =
    userPosition
      ? haversineMeters(userPosition.lat, userPosition.lon, poi.lat, poi.lon)
      : null

  const getWikiLink = () => {
    if (poi.source === 'wiki' && poi.domain && poi.pageid) {
      return `https://${poi.domain}/wiki/${encodeURIComponent(poi.title)}`
    }
    if (poi.source === 'osm' && poi.osm?.wikipedia) {
      const parts = poi.osm.wikipedia.split(':')
      const lang = parts[0] || language
      const title = parts.slice(1).join(':')
      return `https://${lang}.wikipedia.org/wiki/${encodeURIComponent(title)}`
    }
    if (poi.source === 'osm' && poi.osm?.wikidata) {
      return `https://www.wikidata.org/wiki/${poi.osm.wikidata}`
    }
    if (poi.source === 'wdnature') {
      return `https://www.wikidata.org/wiki/${poi.key.replace('wdn:', '')}`
    }
    return null
  }

  const getOSMLink = () => {
    if (poi.source === 'osm' && poi.osm) {
      return `https://www.openstreetmap.org/${poi.osm.osmType}/${poi.osm.id}`
    }
    return null
  }

  const getGoogleMapsLink = () => {
    return `https://www.google.com/maps/dir/?api=1&destination=${poi.lat},${poi.lon}`
  }

  const wikiLink = getWikiLink()
  const osmLink = getOSMLink()
  const googleMapsLink = getGoogleMapsLink()

  // Format bearing to compass direction
  const getBearingText = (bearing?: number) => {
    if (bearing === undefined) return null
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
    const index = Math.round(bearing / 22.5) % 16
    return `${directions[index]} (${Math.round(bearing)}°)`
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-[999] backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Centered Modal */}
      <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 pointer-events-none">
        <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl pointer-events-auto">
          {/* Header */}
          <div className="flex items-start justify-between mb-4 pb-4 border-b border-border">
            <div className="flex-1 pr-4">
              <h3 className="text-xl font-bold text-text mb-1">{poi.title}</h3>
              {poi.desc && (
                <p className="text-sm text-text-muted leading-relaxed">{poi.desc}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text transition-colors flex-shrink-0 p-1 hover:bg-background-elevated rounded"
              aria-label="Close"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            {/* Key Information Grid */}
            <div className="grid grid-cols-2 gap-3">
              {/* Distance */}
              {distance != null && (
                <div className="flex items-center gap-2 p-2.5 bg-background-elevated rounded-lg">
                  <Icon name="location" className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <div className="text-xs text-text-muted">Distance</div>
                    <div className="text-sm font-medium">
                      {distance >= 1000
                        ? `${(distance / 1000).toFixed(2)} km`
                        : `${Math.round(distance)} m`}
                    </div>
                  </div>
                </div>
              )}

              {/* Bearing/Direction */}
              {poi.bearing !== undefined && (
                <div className="flex items-center gap-2 p-2.5 bg-background-elevated rounded-lg">
                  <Icon name="compass" className="w-5 h-5 text-primary flex-shrink-0" />
                  <div>
                    <div className="text-xs text-text-muted">Direction</div>
                    <div className="text-sm font-medium">{getBearingText(poi.bearing)}</div>
                  </div>
                </div>
              )}

              {/* Rating */}
              {poi.rating && (
                <div className="flex items-center gap-2 p-2.5 bg-background-elevated rounded-lg">
                  <span className="text-xl">⭐</span>
                  <div>
                    <div className="text-xs text-text-muted">Rating</div>
                    <div className="text-sm font-medium">
                      {poi.rating.toFixed(1)}
                      {poi.userRatings && (
                        <span className="text-xs text-text-muted ml-1">
                          ({poi.userRatings})
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Source */}
              <div className="flex items-center gap-2 p-2.5 bg-background-elevated rounded-lg">
                <Icon name="info" className="w-5 h-5 text-primary flex-shrink-0" />
                <div>
                  <div className="text-xs text-text-muted">Source</div>
                  <div className="text-sm font-medium">
                    {poi.source === 'wiki'
                      ? 'Wikipedia'
                      : poi.source === 'osm'
                      ? 'OpenStreetMap'
                      : poi.source === 'wdnature'
                      ? 'Wikidata'
                      : 'Google Places'}
                  </div>
                </div>
              </div>
            </div>

            {/* Coordinates */}
            <div className="p-3 bg-background-elevated rounded-lg">
              <div className="text-xs text-text-muted mb-1">Coordinates</div>
              <div className="text-sm font-mono">
                {poi.lat.toFixed(6)}, {poi.lon.toFixed(6)}
              </div>
            </div>

            {/* Types (Google Places) */}
            {poi.types && poi.types.length > 0 && (
              <div className="p-3 bg-background-elevated rounded-lg">
                <div className="text-xs text-text-muted mb-2">Categories</div>
                <div className="flex flex-wrap gap-1.5">
                  {poi.types.slice(0, 6).map((type, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 text-xs bg-background rounded border border-border"
                    >
                      {type.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* OSM Tags */}
            {poi.osm?.tags && Object.keys(poi.osm.tags).length > 0 && (
              <div className="p-3 bg-background-elevated rounded-lg">
                <div className="text-xs text-text-muted mb-2">Tags (OpenStreetMap)</div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {Object.entries(poi.osm.tags)
                    .filter(([key]) => !key.startsWith('name:') && key !== 'name')
                    .slice(0, 8)
                    .map(([key, value]) => (
                      <div key={key} className="flex gap-2 text-xs">
                        <span className="text-text-muted font-medium min-w-[80px]">{key}:</span>
                        <span className="text-text">{value}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-2 pt-4 border-t border-border">
              {/* Primary Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => onRoute(poi)}
                  variant="primary"
                  size="sm"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Icon name="route" className="w-4 h-4 flex-shrink-0" />
                  <span>Plan Route</span>
                </Button>

                {onNarrate && (
                  <Button
                    onClick={() => onNarrate(poi)}
                    variant="primary"
                    size="sm"
                    disabled={isNarrating}
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    {isNarrating ? (
                      <>
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                        </svg>
                        <span>Read Aloud</span>
                      </>
                    )}
                  </Button>
                )}
              </div>

              {/* Secondary Actions */}
              <div className="flex flex-wrap gap-2">
                {wikiLink && (
                  <Button
                    onClick={() => window.open(wikiLink, '_blank', 'noopener,noreferrer')}
                    variant="secondary"
                    size="sm"
                    className="flex-1 flex items-center justify-center gap-2"
                  >
                    <Icon name="wikipedia" className="w-4 h-4 flex-shrink-0" />
                    <span>Wikipedia</span>
                  </Button>
                )}

                {osmLink && (
                  <Button
                    onClick={() => window.open(osmLink, '_blank', 'noopener,noreferrer')}
                    variant="secondary"
                    size="sm"
                    className="flex items-center justify-center gap-2"
                  >
                    <Icon name="map" className="w-4 h-4 flex-shrink-0" />
                    <span>OSM</span>
                  </Button>
                )}

                <Button
                  onClick={() => window.open(googleMapsLink, '_blank', 'noopener,noreferrer')}
                  variant="secondary"
                  size="sm"
                  className="flex items-center justify-center gap-2"
                >
                  <Icon name="location" className="w-4 h-4 flex-shrink-0" />
                  <span>Google Maps</span>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  )
}
